import { observer } from "mobx-react-lite";

import { PatientAISummary, PermissionBit } from "../../api";
import { hasMonitoringAccess } from "../../constants/permissions";
import { AiSummaryContent } from "../../features/patient/ai-summary/summary-body";
import { AiSummaryFeedback } from "../../features/patient/ai-summary/summary-feedback";
import { CellExpandableLine } from "../ui-kit/tables/cells/cell-expandable-line";

/**
 * The patient's AI summary in a waiting-room row: the model's heading on one
 * ellipsized line, expandable to the heading, the facts it was drawn from and
 * its reasoning, with the reviewer's like or dislike of it below.
 *
 * db-api stores the two written fields and the time they were generated, so
 * this is plain text rather than markdown. Either field may be null on its
 * own, and a row with neither renders the empty cell.
 *
 * The facts are read when the popover opens, for that one patient: prefetching
 * them for every listed row would be six requests per row.
 */
export const CellAiSummary = observer(function CellAiSummary(props: {
  patientId: number;
  permissions: PermissionBit[];
  summary?: PatientAISummary | null;
  userUuid: string;
  /** Column width in px; caps the cell so a long heading can't stretch the column. */
  width?: number;
}) {
  const summary = props.summary;

  return (
    <CellExpandableLine
      line={summary?.heading?.trim() || summary?.reasoning?.trim()}
      style={props.width != null ? { maxWidth: props.width } : undefined}
      popoverClassName="max-h-[min(var(--radix-popover-content-available-height),70vh)] w-[28rem] max-w-[90vw] overflow-auto"
      // Beside the row rather than below it, the popover can slide up and down
      // the viewport, so the feedback form gets the full height wherever the
      // row sits, however tall the content has grown since it opened.
      popoverSide="right"
    >
      <div className="flex flex-col gap-3 text-sm">
        <AiSummaryContent
          patientId={props.patientId}
          permissions={props.permissions}
          summary={summary}
        />
        {summary && hasMonitoringAccess(props.permissions) && (
          <AiSummaryFeedback summary={summary} userUuid={props.userUuid} />
        )}
      </div>
    </CellExpandableLine>
  );
});
